import { Link } from "react-router-dom";
import { usePropertiesQuery } from "@/hooks/usePropertiesQuery";
import { PropertyCard } from "@/components/PropertyCard";
import type { PropertyCardData } from "@/components/PropertyCard";
import { Typography } from "@/components/ui/Typography";

function getRentStatus(
  status: string,
  dueDate: number,
  currentDay: number
): "PENDING" | "PAID" | "AWAITING_VERIFICATION" | "OVERDUE" {
  if (status === "PAID") return "PAID";
  if (status === "AWAITING_VERIFICATION") return "AWAITING_VERIFICATION";
  if (status === "PENDING" && currentDay > dueDate) return "OVERDUE";
  return "PENDING";
}

export function PropertiesPage() {
  const { properties, rentRecords, loading, error, refetch } = usePropertiesQuery();
  const currentDay = new Date().getDate();

  const rentByUnitId = new Map(rentRecords.map((r) => [r.roomId, r]));

  const propertyCards: PropertyCardData[] = properties.map((p) => {
    const units = p.units ?? [];
    const unitStatuses = units.map((u) => {
      const rent = rentByUnitId.get(u.id);
      const dueDate = u.dueDate ?? 1;
      return rent ? getRentStatus(rent.status, dueDate, currentDay) : "PENDING";
    });
    const hasOverdue = unitStatuses.includes("OVERDUE");
    const hasPending = unitStatuses.includes("PENDING");
    const allPaid = unitStatuses.length > 0 && unitStatuses.every((s) => s === "PAID");
    const rentStatus = hasOverdue ? "OVERDUE" : hasPending ? "PENDING" : allPaid ? "PAID" : undefined;

    return {
      id: p.id,
      name: p.name,
      address: p.address,
      totalRent: p.totalRent,
      units: units.map((u) => ({
        id: u.id,
        alias: u.alias,
        unitNumber: u.unitNumber,
        dueDate: u.dueDate ?? 1,
        tenants: u.tenants.map((t) => ({ id: t.id, name: t.name })),
      })),
      rentStatus,
    };
  });

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between gap-4">
          <div className="h-8 w-32 bg-slate-200 dark:bg-slate-700 rounded-lg animate-pulse" />
          <div className="h-10 w-28 bg-slate-200 dark:bg-slate-700 rounded-xl animate-pulse" />
        </div>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-48 bg-slate-200 dark:bg-slate-700 rounded-2xl animate-pulse" />
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-2xl border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 p-6 text-center">
        <p className="text-red-700 dark:text-red-300 font-medium">{error instanceof Error ? error.message : String(error)}</p>
        <p className="mt-2 text-sm text-red-600 dark:text-red-400">Please try again later.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-4">
        <div className="min-w-0">
          <Typography variant="h1">Properties</Typography>
          <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
            Manage your rental properties, units, and tenants.
          </p>
        </div>
        {propertyCards.length > 0 && (
          <Link
            to="/properties/new"
            className="shrink-0 whitespace-nowrap rounded-xl bg-primary-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm shadow-primary-600/20 hover:bg-primary-700 active:scale-[0.98] transition-all flex items-center gap-2 min-h-[44px]"
          >
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
            </svg>
            <span className="hidden sm:inline">Add Property</span>
            <span className="sm:hidden">Add</span>
          </Link>
        )}
      </div>

      {propertyCards.length === 0 ? (
        <div className="rounded-2xl border border-slate-200 dark:border-slate-700/80 bg-white dark:bg-slate-800/95 p-10 sm:p-16 text-center shadow-sm dark:shadow-none">
          <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl bg-primary-50 dark:bg-primary-900/30">
            <svg
              className="h-8 w-8 text-primary-500 dark:text-primary-400"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={1.5}
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M8.25 21v-4.875c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21m0 0h4.5V3.545M12.75 21h7.5V10.75M2.25 21h1.5m18 0h-18M2.25 9l4.5-1.636M18.75 3l-1.5.545m0 6.205 3 1m1.5.5-1.5-.5M6.75 7.364V3h-3v18m3-13.636 10.5-3.819"
              />
            </svg>
          </div>
          <Typography variant="h3" className="mt-5">No properties yet</Typography>
          <p className="mt-2 text-sm text-slate-500 dark:text-slate-400 max-w-sm mx-auto leading-relaxed">
            Start by adding your first rental property with units and tenants.
          </p>
          <Link
            to="/properties/new"
            className="mt-6 inline-flex items-center gap-2 rounded-xl bg-primary-600 px-5 py-2.5 text-sm font-semibold text-white shadow-sm shadow-primary-600/20 hover:bg-primary-700 active:scale-[0.98] transition-all min-h-[44px]"
          >
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
            </svg>
            Add your first property
          </Link>
        </div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {propertyCards.map((property) => (
            <PropertyCard key={property.id} property={property} onArchive={refetch} />
          ))}
        </div>
      )}
    </div>
  );
}
